
import React, { useState, useEffect } from 'react';
import { Modal, FormInput } from '../../../components/ui';
import { fmt } from '../../../lib/formatters';
import { Wallet, CheckCircle2, AlertCircle, CreditCard, Landmark, Banknote } from 'lucide-react';
import toast from 'react-hot-toast';

const PurchaseReturnPaymentModal = ({ isOpen, onClose, returnData, onConfirm }) => {
    const totalAmount = Number(returnData?.totalAmount || returnData?.amount || 0);
    const receivedSoFar = Number(returnData?.receivedAmount || returnData?.refundAmount || 0);
    const balance = Math.max(0, totalAmount - receivedSoFar);

    const [amount, setAmount] = useState('');
    const [method, setMethod] = useState('bank_transfer');
    const [transactionId, setTransactionId] = useState('');
    const [bankName, setBankName] = useState('');
    const [branch, setBranch] = useState('');
    const [upiPhone, setUpiPhone] = useState('');
    const [receiverName, setReceiverName] = useState('');
    const [receiverPhone, setReceiverPhone] = useState('');
    const [chequeNumber, setChequeNumber] = useState('');
    const [chequeDate, setChequeDate] = useState('');
    const [remarks, setRemarks] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setAmount(balance.toString());
            setMethod('bank_transfer');
            setTransactionId('');
            setBankName('');
            setBranch('');
            setUpiPhone('');
            setReceiverName('');
            setReceiverPhone('');
            setChequeNumber('');
            setChequeDate('');
            setRemarks('');
        }
    }, [isOpen, balance]);

    const handleSubmit = async () => {
        const payAmount = Number(amount);
        if (!payAmount || payAmount <= 0) {
            return toast.error("Please enter a valid payment amount");
        }
        if (payAmount > balance) {
            return toast.error(`Payment cannot exceed remaining balance (${fmt(balance)})`);
        }

        setLoading(true);
        try {
            await onConfirm(returnData, {
                amount: payAmount,
                method,
                transactionId,
                refId: transactionId,
                bankName,
                branch,
                upiPhone,
                receiverName,
                receiverPhone,
                chequeNumber,
                chequeDate,
                remarks
            });
            onClose();
        } catch (err) {
            console.error(err);
            toast.error("Failed to record payment");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={
                <div className="flex flex-col gap-0.5">
                    <span className="text-[14px] font-bold text-white font-serif tracking-widest uppercase">
                        Verve Kitchens
                    </span>
                    <span className="text-[9px] text-slate-300 font-semibold tracking-widest uppercase">
                        Record Refund Payment
                    </span>
                </div>
            }
            size="max-w-md"
        >
            <div className="space-y-4">
                {/* Header Info */}
                <div className="p-3 bg-emerald-50/50 rounded-xl border border-emerald-100 flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 flex-shrink-0">
                        <Wallet size={20} />
                    </div>
                    <div>
                        <h4 className="text-[11px] font-bold text-emerald-800 tracking-wider uppercase">Refund Payment Entry</h4>
                        <p className="text-[10px] font-medium text-emerald-600 uppercase">ID: {returnData?.returnNo || returnData?.returnId || 'PR-301431'}</p>
                    </div>
                </div>

                {/* Balance Stats Grid */}
                <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-slate-50/50 rounded-xl border border-slate-100">
                        <span className="text-slate-700 font-bold text-[12px] tracking-wide mb-1 block">Total Return</span>
                        <span className="text-[12px] font-medium text-slate-800 truncate leading-snug block">{fmt(totalAmount)}</span>
                    </div>
                    <div className="p-3 bg-blue-50/30 rounded-xl border border-blue-100">
                        <span className="text-slate-700 font-bold text-[12px] tracking-wide mb-1 block">Balance Due</span>
                        <span className="text-[12px] font-medium text-brand-blue truncate leading-snug block">{fmt(balance)}</span>
                    </div>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="text-slate-700 font-bold text-[12px] tracking-wide mb-1 block">Amount Received (₹) *</label>
                        <input
                            type="number"
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 font-medium focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition-all"
                            placeholder="0.00"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="text-slate-700 font-bold text-[12px] tracking-wide mb-1 block">Payment Mode</label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {[
                                { id: 'bank_transfer', label: 'Bank', icon: <Landmark size={14} /> },
                                { id: 'upi_qr', label: 'UPI', icon: <CreditCard size={14} /> },
                                { id: 'cash', label: 'Cash', icon: <Banknote size={14} /> },
                                { id: 'cheque', label: 'Cheque', icon: <CheckCircle2 size={14} /> }
                            ].map(m => (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => setMethod(m.id)}
                                    className={`flex flex-col items-center justify-center gap-1 py-2 rounded-xl border-2 transition-all ${
                                        method === m.id 
                                        ? 'bg-brand-blue/5 border-brand-blue text-brand-blue shadow-sm' 
                                        : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'
                                    }`}
                                >
                                    {m.icon}
                                    <span className="text-[10px] font-bold uppercase tracking-wider">{m.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Dynamic Fields */}
                    {method === 'bank_transfer' && (
                        <div className="grid grid-cols-1 gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                            <FormInput 
                                label="Transaction / Ref ID"
                                placeholder="TRX-123..."
                                value={transactionId}
                                onChange={(e) => setTransactionId(e.target.value)}
                            />
                            <div className="grid grid-cols-2 gap-3">
                                <FormInput 
                                    label="Bank Name"
                                    placeholder="HDFC, SBI..."
                                    value={bankName}
                                    onChange={(e) => setBankName(e.target.value)}
                                />
                                <FormInput 
                                    label="Branch"
                                    placeholder="Branch Name"
                                    value={branch}
                                    onChange={(e) => setBranch(e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    {method === 'upi_qr' && (
                        <div className="grid grid-cols-1 gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                            <FormInput 
                                label="UPI UTR / Ref No."
                                placeholder="TRX-123..."
                                value={transactionId}
                                onChange={(e) => setTransactionId(e.target.value)}
                            />
                            <FormInput 
                                label="UPI Phone Number"
                                placeholder="10-digit number"
                                value={upiPhone}
                                onChange={(e) => setUpiPhone(e.target.value)}
                            />
                        </div>
                    )}

                    {method === 'cash' && (
                        <div className="grid grid-cols-2 gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                            <FormInput 
                                label="Receiver Name"
                                placeholder="Who received"
                                value={receiverName}
                                onChange={(e) => setReceiverName(e.target.value)}
                            />
                            <FormInput 
                                label="Receiver Phone"
                                placeholder="10-digit number"
                                value={receiverPhone}
                                onChange={(e) => setReceiverPhone(e.target.value)}
                            />
                        </div>
                    )}

                    {method === 'cheque' && (
                        <div className="grid grid-cols-1 gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                            <div className="grid grid-cols-2 gap-3">
                                <FormInput 
                                    label="Cheque Number"
                                    placeholder="6-digit No."
                                    value={chequeNumber}
                                    onChange={(e) => setChequeNumber(e.target.value)}
                                />
                                <FormInput 
                                    label="Cheque Date"
                                    type="date"
                                    value={chequeDate}
                                    onChange={(e) => setChequeDate(e.target.value)}
                                />
                            </div>
                            <FormInput 
                                label="Issuing Bank"
                                placeholder="Bank name"
                                value={bankName}
                                onChange={(e) => setBankName(e.target.value)}
                            />
                        </div>
                    )}

                    <div>
                        <label className="text-slate-700 font-bold text-[12px] tracking-wide mb-1 block">Internal Remarks</label>
                        <textarea
                            rows={2}
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 font-medium focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition-all resize-none"
                            placeholder="Optional notes..."
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                        />
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="flex gap-3 pt-2">
                    <button
                        className="flex-1 py-2.5 border border-slate-200 rounded-lg text-xs text-slate-600 font-bold hover:bg-slate-50 transition-colors"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Discard
                    </button>
                    <button
                        className="flex-1 py-2.5 bg-slate-900 hover:bg-black text-white rounded-md text-xs font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                        onClick={handleSubmit}
                        disabled={loading || !amount || Number(amount) <= 0}
                    >
                        {loading ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <>
                                <CheckCircle2 size={14} />
                                Record Payout
                            </>
                        )}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export { PurchaseReturnPaymentModal };


